let csrfToken = null;
let csrfRequest = null;
let requestEpoch = 0;
const pendingReads = new Map();
const italianErrors = {
  VIEW_FILTER_INVALID: 'I filtri della vista non sono validi. Correggi la vista.',
  UNAUTHENTICATED: 'È necessario accedere.', FORBIDDEN: 'Non hai i permessi per eseguire questa azione.', CSRF_INVALID: 'La sessione di sicurezza è scaduta. Ricarica la pagina.',
  TICKET_NOT_FOUND: 'Ticket non trovato.', VIEW_NOT_FOUND: 'Vista non trovata.', VIEW_FORBIDDEN: 'Non hai accesso a questa vista.', VERSION_CONFLICT: 'Il ticket è stato modificato da un altro utente. Aggiorna e riprova.',
  LOGIN_FAILED: 'Email, nome utente o password non corretti.', MAILBOX_REQUIRED: 'Questo ticket non ha una casella mittente attiva.', SHOPIFY_NOT_CONFIGURED: 'Shopify non è configurato.',
  CURSOR_INVALID: 'Il cursore di paginazione non è valido.', CUSTOM_FIELD_INVALID: 'Uno dei campi personalizzati non è valido.', FIELD_LIMIT: 'Sono consentiti al massimo 25 campi attivi.', LAST_ADMIN: 'Non è possibile disattivare o declassare l’ultimo amministratore.', PROTECTED_ADMIN: 'L’amministratore protetto non può essere disabilitato, declassato o modificato.',
  LOCAL_ACCOUNT_LIMIT: 'È consentito un solo account locale di emergenza.', MESSAGE_NOT_RETRYABLE: 'Solo i messaggi non riusciti possono essere reinviati.', SHOPIFY_FULL_SYNC_DISABLED: 'L’importazione completa Shopify è disattivata.',
  IMPORT_ALREADY_ACTIVE: 'È già attiva una migrazione Gorgias.', FULL_IMPORT_REQUIRED: 'Serve un’importazione completa riuscita prima del cutoff.', GORGIAS_CUTOVER_COMPLETE: 'Il passaggio definitivo da Gorgias è già stato completato.', GORGIAS_CUTOVER_BLOCKED: 'Il cutoff è sospeso perché il delta contiene errori bloccanti.', IMPORT_NOT_RETRYABLE: 'Non ci sono errori bloccanti recuperabili.', IMPORT_NOT_RECONCILABLE: 'Serve prima un’importazione Gorgias reale riuscita.',
  GORGIAS_IMPORT_DISABLED: 'L’accesso alla migrazione Gorgias non è attivo.', GORGIAS_CLOSURE_RECONCILIATION_ACTIVE: 'È già attivo un controllo delle chiusure Gorgias.', GORGIAS_CLOSURE_PREVIEW_REQUIRED: 'Esegui prima una nuova anteprima delle chiusure Gorgias.', GORGIAS_CLOSURE_PREVIEW_STALE: 'L’anteprima delle chiusure Gorgias è scaduta: esegui un nuovo controllo.',
  INBOX_PREFERENCES_INVALID: 'La disposizione personale contiene elementi o colori non validi.', MERGE_INVALID: 'I ticket selezionati non possono essere consolidati.', VIEW_TEAM_REQUIRED: 'Seleziona il team della vista.', AUTO_TAG_CONDITIONS_INVALID: 'Aggiungi da una a venti condizioni.', AUTO_TAG_CONDITION_INVALID: 'Controlla i valori delle condizioni.', AUTO_TAG_REGEX_UNSAFE: 'L’espressione regolare non è sicura o è troppo complessa.', AUTO_TAG_RUN_INVALID: 'La rielaborazione non può essere ripresa: avviane una nuova.',
  SHIPPING_INCIDENT_NOT_FOUND: 'Problema di spedizione non trovato.', SHIPPING_INCIDENT_DUPLICATE: 'Esiste già un caso attivo simile. Inserisci un motivo per crearne un altro.', SHIPPING_CONTEXT_INVALID: 'Il ticket, l’ordine o la spedizione selezionati non sono validi.', SHIPPING_TRANSITION_INVALID: 'Questa transizione del reclamo non è consentita.', SHIPPING_INCIDENT_NOT_SETTLED: 'Il caso contiene ancora reclami o rimborsi da completare.'
};

async function parseResponse(response) {
  if (response.status === 204) return null;
  const payload = await response.json().catch(() => ({ error: { message: 'Il server ha restituito una risposta non leggibile.' } }));
  if (!response.ok) {
    const error = new Error(italianErrors[payload.error?.code] || payload.error?.message || `Richiesta non riuscita (${response.status})`);
    error.code = payload.error?.code;
    error.status = response.status;
    error.meta = payload.meta;
    throw error;
  }
  return payload;
}

export async function ensureCsrf() {
  if (csrfToken) return csrfToken;
  if (csrfRequest) return csrfRequest;
  const epoch = requestEpoch;
  const task = (async () => {
    const payload = await parseResponse(await fetch('/api/v1/auth/csrf', { credentials: 'same-origin' }));
    if (epoch === requestEpoch) csrfToken = payload.data.csrfToken;
    return payload.data.csrfToken;
  })().finally(() => { if (csrfRequest === task) csrfRequest = null; });
  csrfRequest = task;
  return task;
}

export async function api(path, options = {}) {
  if ((options.method || 'GET').toUpperCase() !== 'GET') return request(path, options);
  if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const { signal, ...requestOptions } = options;
  const key = JSON.stringify([requestEpoch, path, [...new Headers(options.headers || {})], options.cache, options.mode, options.redirect]);
  let entry = pendingReads.get(key);
  if (!entry) {
    const controller = new AbortController();
    entry = { controller, subscribers: new Set() };
    entry.task = request(path, { ...requestOptions, signal: controller.signal }).finally(() => { if (pendingReads.get(key) === entry) pendingReads.delete(key); });
    pendingReads.set(key, entry);
  }
  return new Promise((resolve, reject) => {
    const subscriber = {};
    const cleanup = () => { signal?.removeEventListener('abort', subscriber.cancel); entry.subscribers.delete(subscriber); };
    subscriber.cancel = () => {
      cleanup(); reject(new DOMException('Aborted', 'AbortError'));
      if (!entry.subscribers.size) { if (pendingReads.get(key) === entry) pendingReads.delete(key); entry.controller.abort(); }
    };
    entry.subscribers.add(subscriber); signal?.addEventListener('abort', subscriber.cancel, { once: true });
    entry.task.then(value => { if (entry.subscribers.has(subscriber)) { cleanup(); resolve(structuredClone(value)); } }, error => { if (entry.subscribers.has(subscriber)) { cleanup(); reject(error); } });
  });
}

async function request(path, options = {}) {
  const method = options.method || 'GET';
  const headers = new Headers(options.headers || {});
  if (!['GET', 'HEAD'].includes(method.toUpperCase())) headers.set('x-csrf-token', await ensureCsrf());
  if (options.body && !(options.body instanceof FormData)) headers.set('content-type', 'application/json');
  const response = await fetch(`/api/v1${path}`, {
    ...options,
    method,
    headers,
    body: options.body instanceof FormData ? options.body : options.body ? JSON.stringify(options.body) : undefined,
    credentials: 'same-origin'
  });
  if (response.status === 401) window.dispatchEvent(new CustomEvent('session-expired'));
  return parseResponse(response);
}

export async function login(identifier, password) {
  const payload = await api('/auth/login', { method: 'POST', body: { identifier, password } });
  csrfToken = payload.data.csrfToken;
  return payload.data.user;
}

export async function logout() {
  try { await api('/auth/logout', { method: 'POST' }); } finally { resetCsrf(); }
}

export async function downloadFile(path, { signal } = {}) {
  const response = await fetch(`/api/v1${path}`, { credentials: 'same-origin', signal, cache: 'no-store' });
  if (response.status === 401) window.dispatchEvent(new CustomEvent('session-expired'));
  if (!response.ok) return parseResponse(response);
  if (!response.headers.get('content-type')?.startsWith('application/pdf')) throw new Error('Il server non ha restituito un PDF valido. Riprova.');
  const disposition = response.headers.get('content-disposition') || '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : /filename="([^"]+)"/i.exec(disposition)?.[1] || 'Tracking.pdf';
  return { blob: await response.blob(), filename };
}

export function resetCsrf() {
  csrfToken = null; csrfRequest = null; requestEpoch++;
  for (const entry of pendingReads.values()) for (const subscriber of [...entry.subscribers]) subscriber.cancel();
  pendingReads.clear();
}
